<template>
  <div>
    <Navbar>
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 class="mb-0 font-weight-bold">Políticas de Inventario</h5>
              <small class="text-muted">
                Configura los parámetros que controlan el cálculo de
                recomendaciones de compra
              </small>
            </div>
            <div>
              <button
                class="btn btn-outline-info btn-sm mr-2"
                @click="showHelp = !showHelp"
              >
                <i class="fa fa-question-circle mr-1"></i>
                {{ showHelp ? "Ocultar guía" : "Guía de parámetros" }}
              </button>
              <button class="btn btn-primary btn-sm" @click="openCreate">
                <i class="fa fa-plus mr-1"></i> Nueva Política
              </button>
            </div>
          </div>

          <div
            v-show="showHelp"
            class="mb-4 p-3 rounded"
            style="font-size: 0.875rem; background: #f8f9fa; border-left: 4px solid #17a2b8; color: #212529;"
          >
            <h6 class="font-weight-bold mb-3">
              <i class="fa fa-info-circle mr-1"></i> ¿Cómo funcionan los
              parámetros?
            </h6>
            <p class="mb-2 text-muted small">
              Cada política define cómo el motor de recomendaciones calcula
              cuánto stock pedir. Una política puede agrupar
              <strong>varios productos</strong> que comparten la misma
              configuración. Solo los productos asociados a una política
              reciben recomendaciones. Adicionalmente puedes marcar una
              política como <strong>predeterminada</strong> para usarla como
              configuración base de referencia.
            </p>
            <div class="row">
              <div class="col-md-6">
                <table class="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td
                        class="font-weight-bold text-nowrap pr-3"
                        style="width: 1%"
                      >
                        Lead Time
                      </td>
                      <td>
                        Días que transcurren entre que haces el pedido y lo
                        recibes. Si tardas 3 días en recibir mercancía, pon
                        <strong>3</strong>.
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-nowrap pr-3">
                        Cobertura
                      </td>
                      <td>
                        Cuántos días de ventas quieres tener en stock como
                        objetivo. Con <strong>21</strong> siempre tendrás ≈3
                        semanas de inventario.
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-nowrap pr-3">
                        Nivel Servicio Z
                      </td>
                      <td>
                        Factor estadístico de seguridad ante variaciones de
                        demanda. <strong>1.28</strong> = 90%,
                        <strong>1.65</strong> = 95%, <strong>2.05</strong> =
                        98%.
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-nowrap pr-3">
                        Días de Análisis
                      </td>
                      <td>
                        Período histórico de ventas que se analiza para calcular
                        la demanda promedio. <strong>90</strong> días es el
                        valor recomendado.
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-nowrap pr-3">
                        Días mín. con ventas
                      </td>
                      <td>
                        Mínimo de días con ventas registradas dentro del período
                        de análisis para que el cálculo sea confiable.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="col-md-6">
                <table class="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td
                        class="font-weight-bold text-nowrap pr-3"
                        style="width: 1%"
                      >
                        Surge IA
                      </td>
                      <td>
                        Activa el ajuste inteligente: si la IA detecta un patrón
                        de demanda inusualmente alto, incrementa la cantidad
                        sugerida automáticamente.
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-nowrap pr-3">
                        Lookback Surge
                      </td>
                      <td>
                        Ventana de días recientes que la IA analiza para
                        detectar picos de demanda. <strong>14</strong> días es
                        lo habitual.
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-nowrap pr-3">
                        Cap Surge
                      </td>
                      <td>
                        Límite máximo del multiplicador que puede aplicar la IA.
                        <strong>1.5</strong> significa que nunca sugerirá más
                        del 50% extra sobre la cantidad base.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div
                  class="mt-2 p-2 rounded"
                  style="background: #e9ecef; color: #495057;"
                >
                  <small
                    ><strong>Ejemplo:</strong> Con Lead Time 3, Cobertura 21 y
                    ventas promedio de 10 unidades/día, el sistema sugerirá
                    pedir ~240 unidades (21 días × 10 + stock de seguridad para
                    3 días de lead time).</small
                  >
                </div>
              </div>
            </div>
          </div>

          <HorizontalLoader v-if="loading" />

          <div v-if="!loading && error" class="alert alert-danger">
            {{ error }}
          </div>

          <div v-if="!loading && !error">
            <div
              v-if="policies.length === 0"
              class="text-center py-5 text-muted"
            >
              <i class="fa fa-cog fa-3x mb-3 d-block"></i>
              No hay políticas configuradas. Crea una política y asocia los
              productos que deseas monitorear para empezar a recibir
              recomendaciones.
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover table-sm">
                <thead class="thead-light">
                  <tr>
                    <th>Alcance</th>
                    <th class="text-right">Lead Time (días)</th>
                    <th class="text-right">Cobertura (días)</th>
                    <th class="text-right">Nivel Servicio Z</th>
                    <th class="text-right">Días Análisis</th>
                    <th class="text-center">Surge IA</th>
                    <th class="text-right">Lookback Surge</th>
                    <th class="text-right">Cap Surge</th>
                    <th class="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="policy in policies" :key="policy.Id">
                    <td>
                      <span v-if="policy.IsDefault" class="badge badge-secondary"
                        >Predeterminada</span
                      >
                      <span
                        v-else-if="
                          policy.Products && policy.Products.length > 0
                        "
                      >
                        <span
                          v-for="pp in policy.Products"
                          :key="pp.Id || pp.ProductId"
                          class="badge badge-light text-primary mr-1 mb-1"
                          style="font-weight: 600"
                        >
                          {{ productName(pp.ProductId) }}
                        </span>
                      </span>
                      <span v-else class="text-muted small"
                        >Sin productos asociados</span
                      >
                    </td>
                    <td class="text-right">{{ policy.LeadTimeDays }}</td>
                    <td class="text-right">{{ policy.CoverageDays }}</td>
                    <td class="text-right">{{ policy.ServiceLevelZ }}</td>
                    <td class="text-right">{{ policy.AnalysisDays }}</td>
                    <td class="text-center">
                      <i
                        class="fa"
                        :class="
                          policy.SurgeProtectionEnabled
                            ? 'fa-check-circle text-success'
                            : 'fa-times-circle text-muted'
                        "
                      ></i>
                    </td>
                    <td class="text-right">{{ policy.SurgeLookbackDays }}</td>
                    <td class="text-right">{{ policy.SurgeMultiplierCap }}x</td>
                    <td class="text-center">
                      <button
                        class="btn btn-xs btn-outline-secondary mr-1"
                        @click="openEdit(policy)"
                      >
                        <i class="fa fa-pencil"></i>
                      </button>
                      <button
                        class="btn btn-xs btn-outline-danger"
                        @click="confirmDelete(policy)"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal crear/editar -->
      <div v-if="showModal" class="modal-overlay">
        <div
          class="card"
          style="width: 520px; max-height: 90vh; overflow-y: auto"
        >
          <div class="card-body">
            <h6 class="font-weight-bold mb-4">
              {{ editingPolicy ? "Editar Política" : "Nueva Política" }}
            </h6>

            <div class="form-group">
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="isDefaultToggle"
                  v-model="form.IsDefault"
                  @change="onIsDefaultChange"
                />
                <label
                  class="custom-control-label font-weight-bold small"
                  for="isDefaultToggle"
                >
                  Política predeterminada (configuración base de la empresa)
                </label>
              </div>
              <small class="text-muted d-block mt-1">
                Solo puede haber una política predeterminada por empresa. Por
                sí sola no genera recomendaciones, solo sirve como referencia.
              </small>
            </div>

            <div v-if="!form.IsDefault" class="form-group">
              <label class="small font-weight-bold">
                Productos asociados a esta política
              </label>
              <multiselect
                v-model="selectedProducts"
                :options="products"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :placeholder="'Selecciona uno o más productos'"
                label="Name"
                track-by="Id"
                :show-labels="false"
                @input="onProductsSelect"
              />
              <small class="text-muted">
                Solo los productos asociados reciben recomendaciones de stock.
                Un producto solo puede pertenecer a una política.
              </small>
            </div>

            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <label class="small font-weight-bold">Lead Time (días)</label>
                  <input
                    type="number"
                    v-model.number="form.LeadTimeDays"
                    class="form-control form-control-sm"
                    min="1"
                  />
                  <small class="text-muted"
                    >Días entre pedido y recepción</small
                  >
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <label class="small font-weight-bold">Cobertura (días)</label>
                  <input
                    type="number"
                    v-model.number="form.CoverageDays"
                    class="form-control form-control-sm"
                    min="1"
                  />
                  <small class="text-muted"
                    >Stock objetivo en días de venta</small
                  >
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <label class="small font-weight-bold">Nivel Servicio Z</label>
                  <input
                    type="number"
                    v-model.number="form.ServiceLevelZ"
                    class="form-control form-control-sm"
                    step="0.01"
                    min="0"
                  />
                  <small class="text-muted"
                    >Factor estadístico (1.65 = 95%)</small
                  >
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <label class="small font-weight-bold">Días de Análisis</label>
                  <input
                    type="number"
                    v-model.number="form.AnalysisDays"
                    class="form-control form-control-sm"
                    min="1"
                  />
                  <small class="text-muted">Período histórico de ventas</small>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="small font-weight-bold"
                >Días mínimos con ventas</label
              >
              <input
                type="number"
                v-model.number="form.MinSalesDaysRequired"
                class="form-control form-control-sm"
                min="0"
              />
              <small class="text-muted"
                >Mínimo de días con ventas para calcular demanda</small
              >
            </div>

            <div class="form-group">
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="surgeToggle"
                  v-model="form.SurgeProtectionEnabled"
                />
                <label
                  class="custom-control-label font-weight-bold small"
                  for="surgeToggle"
                >
                  Protección Surge (ajuste IA por demanda inusual)
                </label>
              </div>
            </div>

            <div v-if="form.SurgeProtectionEnabled" class="row">
              <div class="col-6">
                <div class="form-group">
                  <label class="small font-weight-bold"
                    >Lookback Surge (días)</label
                  >
                  <input
                    type="number"
                    v-model.number="form.SurgeLookbackDays"
                    class="form-control form-control-sm"
                    min="1"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <label class="small font-weight-bold"
                    >Cap Multiplicador Surge</label
                  >
                  <input
                    type="number"
                    v-model.number="form.SurgeMultiplierCap"
                    class="form-control form-control-sm"
                    step="0.1"
                    min="1"
                  />
                  <small class="text-muted"
                    >Máximo ajuste IA (ej: 1.5 = +50%)</small
                  >
                </div>
              </div>
            </div>

            <div v-if="formError" class="alert alert-danger small py-2">
              {{ formError }}
            </div>

            <div class="d-flex justify-content-end mt-3">
              <button
                class="btn btn-secondary btn-sm mr-2"
                @click="closeModal"
                :disabled="saving"
              >
                Cancelar
              </button>
              <button
                class="btn btn-primary btn-sm"
                @click="savePolicy"
                :disabled="saving"
              >
                <i class="fa fa-save mr-1"></i>
                {{ saving ? "Guardando..." : "Guardar" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Navbar>
  </div>
</template>

<script>
import Navbar from "../layout/Navbar.vue";
import HorizontalLoader from "../shared/HorizontalLoader.vue";
import Multiselect from "vue-multiselect";
import {
  getPolicies,
  createPolicy,
  updatePolicy,
  deletePolicy,
} from "../../services/SmartInventoryService";
import { getProducts } from "../../services/ProductsService";

export default {
  name: "InventoryPolicies",
  components: { Navbar, HorizontalLoader, Multiselect },
  data() {
    return {
      policies: [],
      products: [],
      productMap: {},
      selectedProducts: [],
      showHelp: false,
      loading: false,
      error: null,
      showModal: false,
      editingPolicy: null,
      saving: false,
      formError: null,
      form: this.defaultForm(),
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    defaultForm() {
      return {
        IsDefault: false,
        ProductIds: [],
        LeadTimeDays: 5,
        CoverageDays: 21,
        ServiceLevelZ: 1.65,
        AnalysisDays: 90,
        MinSalesDaysRequired: 10,
        SurgeProtectionEnabled: true,
        SurgeLookbackDays: 14,
        SurgeMultiplierCap: 1.5,
      };
    },
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const companyId = this.$store.getters.getCompany;
        const [policiesRes, prodsRes] = await Promise.all([
          getPolicies(companyId),
          getProducts(companyId, null, null, 1, 1000, 1),
        ]);

        if (policiesRes && policiesRes.ok) {
          this.policies = policiesRes.data || [];
        } else {
          this.error =
            (policiesRes && policiesRes.message) || "Error al cargar políticas";
        }

        const products =
          (prodsRes && prodsRes.data && prodsRes.data.products) || [];
        this.products = products;
        const map = {};
        products.forEach(function (p) {
          map[p.Id] = p.Name;
        });
        this.productMap = map;
      } catch (e) {
        console.log("error ", e);

        this.error = "Error inesperado al cargar los datos";
      } finally {
        this.loading = false;
      }
    },
    productName(productId) {
      return this.productMap[productId] || "Producto #" + productId;
    },
    onProductsSelect(selection) {
      const list = selection || [];
      this.form.ProductIds = list.map(function (p) {
        return p.Id;
      });
    },
    onIsDefaultChange() {
      if (this.form.IsDefault) {
        this.selectedProducts = [];
        this.form.ProductIds = [];
      }
    },
    openCreate() {
      this.editingPolicy = null;
      this.selectedProducts = [];
      this.form = this.defaultForm();
      this.formError = null;
      this.showModal = true;
    },
    openEdit(policy) {
      this.editingPolicy = policy;
      const associatedIds = (policy.Products || []).map(function (pp) {
        return pp.ProductId;
      });
      this.selectedProducts = this.products.filter(function (p) {
        return associatedIds.indexOf(p.Id) !== -1;
      });
      this.form = {
        IsDefault: !!policy.IsDefault,
        ProductIds: associatedIds,
        LeadTimeDays: policy.LeadTimeDays,
        CoverageDays: policy.CoverageDays,
        ServiceLevelZ: policy.ServiceLevelZ,
        AnalysisDays: policy.AnalysisDays,
        MinSalesDaysRequired: policy.MinSalesDaysRequired,
        SurgeProtectionEnabled: policy.SurgeProtectionEnabled,
        SurgeLookbackDays: policy.SurgeLookbackDays,
        SurgeMultiplierCap: policy.SurgeMultiplierCap,
      };
      this.formError = null;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingPolicy = null;
      this.formError = null;
    },
    buildPayload(companyId) {
      const payload = {
        CompanyId: +companyId,
        IsDefault: !!this.form.IsDefault,
        LeadTimeDays: this.form.LeadTimeDays,
        CoverageDays: this.form.CoverageDays,
        ServiceLevelZ: this.form.ServiceLevelZ,
        AnalysisDays: this.form.AnalysisDays,
        MinSalesDaysRequired: this.form.MinSalesDaysRequired,
        SurgeProtectionEnabled: this.form.SurgeProtectionEnabled,
        SurgeLookbackDays: this.form.SurgeLookbackDays,
        SurgeMultiplierCap: this.form.SurgeMultiplierCap,
      };
      if (!this.form.IsDefault) {
        payload.ProductIds = this.form.ProductIds || [];
      }
      return payload;
    },
    async savePolicy() {
      this.formError = null;
      if (!this.form.IsDefault) {
        if (!this.form.ProductIds || this.form.ProductIds.length === 0) {
          this.formError =
            "Debes seleccionar al menos un producto o marcar la política como predeterminada.";
          return;
        }
      }
      this.saving = true;
      try {
        const companyId = this.$store.getters.getCompany;
        const payload = this.buildPayload(companyId);
        let res;
        if (this.editingPolicy) {
          res = await updatePolicy(companyId, this.editingPolicy.Id, payload);
        } else {
          res = await createPolicy(companyId, payload);
        }
        if (res && res.ok) {
          this.closeModal();
          await this.loadData();
        } else {
          this.formError =
            (res && res.message) || "Error al guardar la política";
        }
      } catch (e) {
        this.formError = "Error inesperado al guardar";
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(policy) {
      let scope;
      if (policy.IsDefault) {
        scope = "predeterminada";
      } else if (policy.Products && policy.Products.length > 0) {
        const names = policy.Products.map((pp) =>
          this.productName(pp.ProductId),
        ).join(", ");
        scope = "de los productos " + names;
      } else {
        scope = "#" + policy.Id;
      }
      this.$helpers
        .confirm(
          "¿Eliminar la política " +
            scope +
            "? Esta acción no se puede deshacer.",
        )
        .then((ok) => {
          if (ok) this.deletePolicy(policy);
        });
    },
    async deletePolicy(policy) {
      try {
        const companyId = this.$store.getters.getCompany;
        const res = await deletePolicy(companyId, policy.Id);
        if (res && res.ok) {
          await this.loadData();
        } else {
          this.$helpers.message(
            "Error",
            (res && res.message) || "Error al eliminar la política",
            "error",
          );
        }
      } catch (e) {
        this.$helpers.message("Error", "Error inesperado al eliminar", "error");
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-xs {
  padding: 2px 6px;
  font-size: 11px;
}
</style>
